// vg-binding: P7.D-74
// vg-binding: P7.D-149
//
// apps/vendor/src/features/orders/OrdersTable.tsx
//
// Lightweight orders table for the vendor list page. We intentionally
// skip @tanstack/react-table to avoid a new bundle dep — the row count
// per page is bounded (≤100) so plain table + checkbox-based selection
// is the right size for Wave 6.

import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';

import { cn, VendorStatusBadge } from '@printway/ui';

import { useTranslation } from '../../lib/i18n/index.js';
import { STATUS_PILL_CLASS } from '../../lib/ui/statusPill.js';
import { AssizeModal } from './detail/AssizeModal.js';
import { LabelDetailModal } from './LabelDetailModal.js';
import {
  labelPdfUrl,
  useTransitionItem,
  type OrderStatus,
  type OrdersListItem,
  type SlaColorTier,
} from './api.js';

const CHIP: Record<SlaColorTier, string> = {
  green: STATUS_PILL_CLASS.success,
  yellow: STATUS_PILL_CLASS.progress,
  orange: STATUS_PILL_CLASS.warning,
  red: STATUS_PILL_CLASS.danger,
};

// Order lifecycle + vendor_status badges now render via the canonical
// @printway/ui <OrderStatusBadge> / <VendorStatusBadge> (shared icon + colour
// language). The old per-status class maps lived here — removed 2026-07-01.

function formatDeadline(
  ms: number,
  t: (key: string) => string,
): string {
  const diffH = Math.round((ms - Date.now()) / (60 * 60 * 1000));
  if (diffH <= 0) return t('list.overdue');
  if (diffH < 24) return `${diffH}h`;
  return `${Math.round(diffH / 24)}d`;
}

function formatAssignedAt(ms: number): string {
  if (!ms) return '—';
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    hour12: false,
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface Props {
  rows: OrdersListItem[];
  isLoading: boolean;
  /** Composite key `${order_id}/${item_id}` so the dedupe set is stable. */
  selected: Set<string>;
  onToggle: (item: OrdersListItem) => void;
  onToggleAll: (next: boolean) => void;
  /** R7-2 — open drawer instead of navigating away. */
  onOpenDetail?: (item: OrdersListItem) => void;
}

function rowKey(r: OrdersListItem): string {
  return `${r.order_id}/${r.item_id}`;
}

interface OrderGroup {
  orderId: string;
  orderNumber: string | null;
  items: OrdersListItem[];
}

/** Group flat per-item rows by order_id, preserving first-seen order. */
function groupRowsByOrder(rows: OrdersListItem[]): OrderGroup[] {
  const groups: OrderGroup[] = [];
  const idx = new Map<string, number>();
  for (const r of rows) {
    let i = idx.get(r.order_id);
    if (i === undefined) {
      i = groups.length;
      idx.set(r.order_id, i);
      groups.push({
        orderId: r.order_id,
        orderNumber: r.order_number ?? null,
        items: [],
      });
    }
    groups[i]!.items.push(r);
  }
  return groups;
}

// R10 — simple inline transitions that don't need a modal go through the
// table directly. Anything that requires extra input (dimensions, QC notes,
// shipping tracking number, reject reason) routes the user through the
// drawer where the existing ActionBar + modal stack lives.
// Amendment #9 — the new vendor flow has no separate production
// steps: pushing an order to the vendor IS production; the vendor Assizes when
// done. The production-FSM inline CTAs (Accept / Start Production / QC / Mark
// Shipped / Complete) are removed from the list. Row actions now = Assize +
// Open detail only.
function inlineNextState(_status: OrderStatus): OrderStatus | null {
  return null;
}

// Amendment #9 — production-step CTAs removed (see inlineNextState).
function inlineLabelKey(_status: OrderStatus): string | null {
  return null;
}

export function OrdersTable({
  rows,
  isLoading,
  selected,
  onToggle,
  onToggleAll,
  onOpenDetail,
}: Props): JSX.Element {
  const { t } = useTranslation('orders');
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(rowKey(r)));
  const transitionM = useTransitionItem();
  // R10-fix (2026-05-17) — track which assignment_id is currently mutating so
  // only that row's quick-action button is disabled. Without this, the shared
  // `transitionM.isPending` flag froze every quick-action button in the table
  // as soon as ONE row was clicked.
  const [pendingId, setPendingId] = useState<string | null>(null);
  // Expandable order groups: collapse multi-item orders behind a +/- header.
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(
    () => new Set(),
  );
  const toggleOrder = (orderId: string): void =>
    setExpandedOrders((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  // Amendment #7 — Assize modal target (order+item) or null.
  const [assizeTarget, setAssizeTarget] = useState<{
    orderId: string;
    itemId: string;
  } | null>(null);
  // Label detail modal (preview + Print + Download) for a row with a label.
  const [labelTarget, setLabelTarget] = useState<{
    labelUrl: string;
    trackingNumber: string | null;
  } | null>(null);

  if (isLoading && rows.length === 0) {
    return (
      <div className="flex flex-col gap-2 py-6">
        {Array.from({ length: 6 }).map((_v, i) => (
          <div
            key={i}
            className="h-12 animate-pulse rounded-md border border-pw-border bg-pw-surface"
          />
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-pw-border bg-pw-surface px-3 py-8 text-center text-sm text-pw-text-muted">
        {t('list.emptyTab')}
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full table-auto text-sm">
        <thead className="border-b border-pw-border text-xs uppercase tracking-wider text-pw-text-muted">
          <tr>
            {/* Amendment #7 — row-select checkbox for Bulk Assize TH1. */}
            <th className="px-3 py-2 text-left">
              <input
                type="checkbox"
                data-testid="orders-select-all"
                checked={allSelected}
                onChange={(e) => onToggleAll(e.target.checked)}
                aria-label={t('list.selectAll')}
                className="h-4 w-4 rounded border-pw-border"
              />
            </th>
            <th className="px-3 py-2 text-left">{t('list.headerOrder')}</th>
            <th className="px-3 py-2 text-left">{t('list.headerItem')}</th>
            {/* Amendment #9 — single unified vendor_status column;
                the separate Assize column (amend-7) is removed. */}
            <th className="px-3 py-2 text-left">{t('list.headerStatus')}</th>
            <th className="px-3 py-2 text-left">{t('list.headerSla')}</th>
            <th className="px-3 py-2 text-left">{t('list.headerDeadline')}</th>
            <th className="px-3 py-2 text-left">{t('list.headerAssignedAt')}</th>
            <th className="px-3 py-2 text-left">{t('list.headerBackOrder')}</th>
            <th className="px-3 py-2 text-left">{t('list.headerVersion')}</th>
            <th className="px-3 py-2 text-left">{t('list.headerTracking')}</th>
            <th className="px-3 py-2 text-left">{t('list.headerLabel')}</th>
            {/* R6-5 (2026-05-16) — per-row context action replaces bulk-action UI. */}
            <th className="px-3 py-2 text-right">{t('list.headerActions')}</th>
          </tr>
        </thead>
        <tbody>
          {groupRowsByOrder(rows).map((group) => {
            const multi = group.items.length > 1;
            const open = !multi || expandedOrders.has(group.orderId);
            return (
              <Fragment key={group.orderId}>
                {multi ? (
                  <tr
                    data-testid={`orders-group-${group.orderId}`}
                    className="border-b border-pw-border bg-pw-surface/60"
                  >
                    <td className="px-3 py-2 align-top">
                      <button
                        type="button"
                        onClick={() => toggleOrder(group.orderId)}
                        aria-expanded={open}
                        aria-label={
                          open
                            ? t('list.collapse', { defaultValue: 'Collapse' })
                            : t('list.expand', { defaultValue: 'Expand' })
                        }
                        data-testid={`orders-group-expander-${group.orderId}`}
                        className="inline-flex h-5 w-5 items-center justify-center rounded border border-pw-border text-pw-text-muted hover:bg-pw-bg"
                      >
                        {open ? '−' : '+'}
                      </button>
                    </td>
                    <td colSpan={11} className="px-3 py-2">
                      <span className="font-mono text-xs font-semibold text-pw-text">
                        {group.orderNumber ?? group.orderId.slice(-8)}
                      </span>
                      <span className="ml-2 text-xs text-pw-text-muted">
                        {t('list.itemsCount', { count: group.items.length })}
                      </span>
                    </td>
                  </tr>
                ) : null}
                {open
                  ? group.items.map((r) => {
                      const k = rowKey(r);
                      const isChecked = selected.has(k);
                      return (
              <tr
                key={r.assignment_id}
                data-testid={`orders-row-${r.assignment_id}`}
                className={cn(
                  'border-b border-pw-border transition-colors hover:bg-pw-surface',
                  isChecked && 'bg-pw-primary/5',
                )}
              >
                {/* Amendment #7 — per-row select for Bulk Assize TH1. */}
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    data-testid={`orders-row-select-${r.item_id}`}
                    checked={isChecked}
                    onChange={() => onToggle(r)}
                    aria-label={t('list.selectRow')}
                    className="h-4 w-4 rounded border-pw-border"
                  />
                </td>
                <td className="px-3 py-2 font-mono text-xs">
                  <Link
                    to={`/orders/${r.order_id}/items/${r.item_id}`}
                    className="text-pw-primary hover:underline"
                  >
                    {r.order_id.slice(-8)}
                  </Link>
                </td>
                <td className="px-3 py-2 text-xs">
                  <div className="flex flex-col gap-0.5">
                    {r.product_name ? (
                      <span className="text-pw-text" title={r.item_id}>
                        {r.product_name}
                        {typeof r.quantity === 'number' && r.quantity > 0 ? (
                          <span className="ml-1 text-pw-text-muted">
                            × {r.quantity}
                          </span>
                        ) : null}
                      </span>
                    ) : (
                      <span className="font-mono text-pw-text-muted" title={r.item_id}>
                        {r.item_id.slice(-8)}
                      </span>
                    )}
                    <div className="flex items-center gap-2">
                      {r.sku ? (
                        <span className="font-mono text-[11px] text-pw-text-muted">
                          {r.sku}
                        </span>
                      ) : null}
                      {r.artwork_status ? (
                        <span
                          className={cn(
                            'inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium',
                            r.artwork_status === 'ok'
                              ? STATUS_PILL_CLASS.success
                              : STATUS_PILL_CLASS.warning,
                          )}
                        >
                          {r.artwork_status === 'ok'
                            ? t('list.sub.artworkOk', { defaultValue: 'Artwork' })
                            : t('list.sub.artworkNone', { defaultValue: 'No artwork' })}
                        </span>
                      ) : null}
                    </div>
                    {typeof r.vendor_cost_cents === 'number' && r.vendor_cost_cents > 0 ? (
                      <span className="text-[11px] text-pw-text-muted">
                        {t('list.sub.vendorCost', { defaultValue: 'Cost' })}{' '}
                        ${(r.vendor_cost_cents / 100).toFixed(2)}
                      </span>
                    ) : null}
                  </div>
                </td>
                {/* Amendment #9 — single unified vendor_status column
                    (new → assized → labeled → shipped). Replaces the old
                    production-FSM status pill + the separate Assize column. */}
                <td className="px-3 py-2">
                  {(() => {
                    const vs = r.vendor_status ?? 'new';
                    return (
                      <VendorStatusBadge
                        status={vs}
                        label={t(`list.vendorStatus.${vs}`, vs)}
                        testId={`orders-row-status-${r.assignment_id}`}
                      />
                    );
                  })()}
                </td>
                <td className="px-3 py-2">
                  <span
                    className={cn(
                      'inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold',
                      CHIP[r.sla_color_tier],
                    )}
                  >
                    {r.sla_percent_used}%
                  </span>
                </td>
                <td className="px-3 py-2 text-xs text-pw-text-muted">
                  {formatDeadline(r.effective_deadline, t)}
                </td>
                <td className="px-3 py-2 text-xs text-pw-text-muted">
                  {formatAssignedAt(r.assigned_at)}
                </td>
                <td className="px-3 py-2 text-xs">
                  {r.back_order_used ? (
                    <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold ${STATUS_PILL_CLASS.warning}`}>
                      {t('list.backOrder.used')}
                    </span>
                  ) : r.back_order_enabled ? (
                    <span className="text-pw-text-muted">{t('list.backOrder.available')}</span>
                  ) : (
                    <span className="text-pw-text-muted">—</span>
                  )}
                </td>
                <td className="px-3 py-2 font-mono text-xs text-pw-text-muted">
                  v{r.item_version}
                </td>
                <td className="px-3 py-2 text-xs">
                  {r.tracking_number ? (
                    <span
                      data-testid={`orders-row-tracking-${r.assignment_id}`}
                      className="font-mono text-pw-text"
                      title={r.tracking_number}
                    >
                      {r.tracking_number}
                    </span>
                  ) : (
                    <span className="text-pw-text-muted">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs">
                  {r.label_url ? (
                    <button
                      type="button"
                      onClick={() =>
                        setLabelTarget({
                          // UAT #2042 — render from the on-demand PDF endpoint
                          // (keyed by assignment_id), not the unreachable
                          // snapshot label_url. Presence of label_url still
                          // gates the button (label was bought).
                          labelUrl: labelPdfUrl(r.assignment_id),
                          trackingNumber: r.tracking_number ?? null,
                        })
                      }
                      data-testid={`orders-row-label-${r.assignment_id}`}
                      className="inline-flex items-center gap-1 text-pw-primary hover:underline"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.75"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="h-4 w-4"
                        aria-hidden="true"
                      >
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                      </svg>
                      {t('list.labelPdf')}
                    </button>
                  ) : (
                    <span className="text-pw-text-muted">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  {(() => {
                    const next = inlineNextState(r.status);
                    const labelKey = inlineLabelKey(r.status);
                    const label = labelKey ? t(labelKey) : null;
                    const needsModal = label !== null && next === null;
                    return (
                      <div className="inline-flex items-center gap-1.5">
                        {/* Amendment #7 — Assize packaging dims. */}
                        <button
                          type="button"
                          data-testid={`assize-row-${r.item_id}`}
                          onClick={() =>
                            setAssizeTarget({ orderId: r.order_id, itemId: r.item_id })
                          }
                          className="inline-flex items-center rounded-md border border-pw-border bg-pw-surface px-2.5 py-1 text-xs font-medium text-pw-text hover:bg-pw-bg"
                        >
                          {t('assize.button')}
                        </button>
                        {label !== null && next !== null ? (
                          <button
                            type="button"
                            data-testid={`orders-row-quick-${r.status}-${r.assignment_id}`}
                            disabled={pendingId === r.assignment_id}
                            onClick={async () => {
                              setPendingId(r.assignment_id);
                              try {
                                await transitionM.mutateAsync({
                                  order_id: r.order_id,
                                  item_id: r.item_id,
                                  item_version: r.item_version,
                                  to_state: next,
                                });
                              } finally {
                                setPendingId(null);
                              }
                            }}
                            className="inline-flex items-center rounded-md bg-pw-primary px-2.5 py-1 text-xs font-semibold text-white hover:bg-pw-primary-strong disabled:opacity-60"
                          >
                            {pendingId === r.assignment_id ? '…' : label}
                          </button>
                        ) : null}
                        {needsModal && onOpenDetail ? (
                          <button
                            type="button"
                            data-testid={`orders-row-prompt-${r.status}-${r.assignment_id}`}
                            onClick={() => onOpenDetail(r)}
                            className="inline-flex items-center rounded-md bg-pw-primary px-2.5 py-1 text-xs font-semibold text-white hover:bg-pw-primary-strong"
                          >
                            {label}
                          </button>
                        ) : null}
                        {onOpenDetail ? (
                          <button
                            type="button"
                            onClick={() => onOpenDetail(r)}
                            data-testid={`orders-row-action-${r.assignment_id}`}
                            className="inline-flex items-center rounded-md border border-pw-border bg-pw-surface px-2.5 py-1 text-xs font-medium text-pw-text hover:bg-pw-bg"
                          >
                            {t('list.detailButton')}
                          </button>
                        ) : (
                          <Link
                            to={`/orders/${r.order_id}/items/${r.item_id}`}
                            data-testid={`orders-row-action-${r.assignment_id}`}
                            className="inline-flex items-center rounded-md border border-pw-border bg-pw-surface px-2.5 py-1 text-xs font-medium text-pw-text hover:bg-pw-bg"
                          >
                            {t('list.detailLink')}
                          </Link>
                        )}
                      </div>
                    );
                  })()}
                </td>
              </tr>
                      );
                    })
                  : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>

      {/* Amendment #7 — Assize modal, single instance driven by target. */}
      {assizeTarget ? (
        <AssizeModal
          open
          orderId={assizeTarget.orderId}
          itemId={assizeTarget.itemId}
          onClose={() => setAssizeTarget(null)}
        />
      ) : null}

      {/* Label detail modal — preview + Print + Download for a bought label. */}
      {labelTarget ? (
        <LabelDetailModal
          open
          labelUrl={labelTarget.labelUrl}
          trackingNumber={labelTarget.trackingNumber}
          onClose={() => setLabelTarget(null)}
        />
      ) : null}
    </div>
  );
}

export default OrdersTable;
